/**
 * Keeps an ungraded piece from being created twice in the player's progress.
 *
 * A piece with no progress row yet gets one created by its first ungraded
 * write (when the score has loaded). If the player plays and pauses or leaves
 * before that create has come back, the next write would also be create-shaped
 * (no row in the store yet), and the server, which looks the row up before
 * creating it, could create a second row.
 *
 * So while a create for a piece is pending, further writes for that piece are
 * skipped. Nothing the player played is lost: play time is counted by the
 * play-session events, not by this row; the row only records when the piece
 * was last played and at what tempo, and the pending create already says the
 * piece was played just now. Once the row is in the store, writes go out as
 * updates again. A create that never comes back (the request failed) stops
 * counting as pending after CREATE_PENDING_MS, so the piece can still be
 * created later.
 */

export const CREATE_PENDING_MS = 60_000;

export interface UngradedCreateGuard {
  /**
   * Whether an ungraded write for the piece may go out now. `hasRow`: the
   * store already holds the player's row for the piece (the write is an
   * update). Returning true for a piece with no row records a pending create.
   */
  mayWrite: (repertoireId: number, hasRow: boolean) => boolean;
}

export const createUngradedCreateGuard = (now: () => number = Date.now): UngradedCreateGuard => {
  const pendingSince = new Map<number, number>();
  return {
    mayWrite: (repertoireId, hasRow) => {
      if (hasRow) {
        pendingSince.delete(repertoireId);
        return true;
      }
      const since = pendingSince.get(repertoireId);
      if (since !== undefined && now() - since < CREATE_PENDING_MS) return false;
      pendingSince.set(repertoireId, now());
      return true;
    },
  };
};

/** The app's guard: shared by every piece page, so it outlives a remount. */
export const ungradedCreateGuard = createUngradedCreateGuard();
